/**
 * The pilot: the player's ACT account in Vector Drift. One sign-in on every
 * actuniverse.com site (ACT HQ DR-0017): the session sits in a cookie for the
 * whole domain, so a player signed in on the hub or in any ACT game is signed in
 * here too, and a new player gets a guest account made silently, as on the hub
 * and in Firestrike. The name is the ACT name every game shows (`profiles`).
 * Google sign-in, the Account ID and Link Sui are the platform's own Account
 * panel (DR-0053), dressed in Vector Drift's look by profile.css. Online races
 * join the game server with `accessToken()`.
 */
import { ensureGuestSession } from "@act/client-core/auth";
import { supabase } from "@act/client-core/supabase";
import { AccountService, type AccountView } from "@act/client-core-src/account/AccountService";
import { AccountStore } from "@act/client-core-src/account/accountStore";
import { NAME_ERROR_HINTS } from "@act/client-core-src/account/nameHints";
import { getOrCreateGuestId, shortGuestLabel } from "@act/client-core-src/network/guestIdentity";
import { AccountPanel } from "@act/client-core-src/ui/AccountPanel";
import { closeTopModal } from "@act/client-core-src/ui/modalStack";
import { normalizeDisplayName, validateDisplayName } from "@act/shared";

/** "off": the build has no ACT settings (a bare dev build); "offline": sign-in failed. */
export type PilotStatus = "off" | "connecting" | "online" | "offline";

export interface PilotState {
  status: PilotStatus;
  /** The ACT account id; null until signed in. */
  userId: string | null;
  /** The ACT name, or null until one is claimed (the guest label shows meanwhile). */
  name: string | null;
  /** No Google sign-in yet: the account lives in this browser only. */
  guest: boolean;
  /** The account as the Account panel shows it: the Google email, Account ID and linked Sui addresses. */
  account: AccountView;
}

/** The last ACT name seen, so the menu shows it before the account answers. */
const NAME_KEY = "driftracer.pilot.name";

function cachedName(): string | null {
  try {
    return localStorage.getItem(NAME_KEY);
  } catch {
    return null;
  }
}

function cacheName(name: string | null): void {
  try {
    if (name) localStorage.setItem(NAME_KEY, name);
    else localStorage.removeItem(NAME_KEY);
  } catch {
    /* storage blocked — the name still comes from the account */
  }
}

/** One-shot menu navigation from a pad (Menu.handlePad's shape). */
interface Nav {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
  confirm: boolean;
  back: boolean;
}

class Pilot {
  private readonly service = new AccountService(supabase);
  private store: AccountStore | null = null;
  private panel: AccountPanel | null = null;
  private host: HTMLElement | null = null;
  private connecting: Promise<void> | null = null;
  private readonly listeners = new Set<() => void>();
  private readonly nameListeners = new Set<(name: string) => void>();
  /** Pad cursor among the open Account panel's buttons; shown once a pad drives it. */
  private padIndex = 0;
  private padActive = false;
  /** Called by the Account panel's name "change": the profile screen's name field. */
  onEditName: (() => void) | null = null;

  state: PilotState = {
    status: supabase ? "connecting" : "off",
    userId: null,
    name: cachedName(),
    guest: true,
    account: this.service.current(),
  };

  constructor() {
    this.service.onChanged.on((view) => {
      const same = view.userId !== null && view.userId === this.state.userId;
      this.set({ account: view, ...(same ? { guest: !view.signedIn } : {}) });
    });
  }

  /** The name to show: the ACT name, or the guest label every ACT page uses. */
  label(): string {
    return this.state.name ?? shortGuestLabel(getOrCreateGuestId());
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Sign in as this device's ACT account, made as a guest if there isn't one. Off the boot path. */
  connect(): Promise<void> {
    this.connecting ??= this.signIn();
    return this.connecting;
  }

  /** Read the account again (the Google email, Account ID, linked Sui) for the profile. */
  refresh(): void {
    if (this.state.status === "online") void this.service.refresh();
  }

  /** Claim an ACT name. Resolves to the reason it was refused, or null once it's the account's. */
  async setName(raw: string): Promise<string | null> {
    const name = normalizeDisplayName(raw);
    const refused = validateDisplayName(name);
    if (refused) return NAME_ERROR_HINTS[refused];
    if (!name) return NAME_ERROR_HINTS.too_short;
    if (!this.store) return "not signed in yet — try again";
    const saved = await this.store.saveDisplayName(name);
    if (saved === "taken") return NAME_ERROR_HINTS.taken;
    if (saved === "rejected") return NAME_ERROR_HINTS.profanity;
    if (saved === "error") return NAME_ERROR_HINTS.error;
    cacheName(name);
    this.set({ name });
    for (const listener of this.nameListeners) listener(name);
    return null;
  }

  /** The platform's Account panel: Google sign-in, the Account ID, Link Sui, sign out. */
  openAccount(): void {
    if (this.state.status !== "online") return;
    if (!this.host) {
      this.host = document.createElement("div");
      this.host.className = "vd-acct";
      (document.getElementById("app") ?? document.body).appendChild(this.host);
      // The panel redraws itself on every change: put the pad's mark back after each.
      new MutationObserver(() => this.markPad()).observe(this.host, { childList: true, subtree: true });
    }
    this.panel ??= new AccountPanel(this.host, this.service, null, {
      current: () => this.state.name ?? "",
      edit: () => {
        this.panel?.close();
        this.onEditName?.();
      },
      onChanged: (listener) => {
        this.nameListeners.add(listener);
        return () => this.nameListeners.delete(listener);
      },
    });
    this.padIndex = 0;
    this.panel.open();
  }

  accountOpen(): boolean {
    return this.panel?.isOpen() ?? false;
  }

  /** Esc or a pad's B: close the Account panel. */
  closeAccount(): void {
    closeTopModal();
  }

  /** Drive the open Account panel from a pad: up/down pick a button, A presses it, B closes. */
  padAccount(nav: Nav): void {
    if (nav.back) {
      this.closeAccount();
      return;
    }
    const buttons = this.panelButtons();
    if (buttons.length === 0) return;
    this.padActive = true;
    if (nav.up || nav.left) this.padIndex = (this.padIndex - 1 + buttons.length) % buttons.length;
    if (nav.down || nav.right) this.padIndex = (this.padIndex + 1) % buttons.length;
    this.padIndex = Math.min(this.padIndex, buttons.length - 1);
    if (nav.confirm) buttons[this.padIndex]?.click();
    this.markPad();
  }

  /** The account's access token for joining the ACT game server (online races); null offline. */
  async accessToken(): Promise<string | null> {
    return (await ensureGuestSession())?.token ?? null;
  }

  private panelButtons(): HTMLButtonElement[] {
    return Array.from(this.host?.querySelectorAll<HTMLButtonElement>("button") ?? []);
  }

  /** Mark the pad's button, once a pad has driven the panel. */
  private markPad(): void {
    if (!this.padActive) return;
    const buttons = this.panelButtons();
    buttons.forEach((b, i) => {
      const on = i === Math.min(this.padIndex, buttons.length - 1);
      if (b.classList.contains("vd-pad-focus") !== on) b.classList.toggle("vd-pad-focus", on);
    });
  }

  private async signIn(): Promise<void> {
    if (!supabase) return;
    try {
      const session = await ensureGuestSession();
      if (session) {
        this.store = new AccountStore(supabase, session.userId);
        const name = await this.store.fetchDisplayName();
        cacheName(name);
        this.set({ status: "online", userId: session.userId, name, guest: session.isAnonymous });
        return;
      }
    } catch {
      /* falls through to offline */
    }
    this.connecting = null; // let a later visit to the profile try again
    this.set({ status: "offline" });
  }

  private set(patch: Partial<PilotState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }
}

/** This device's pilot (one per page). */
export const pilot = new Pilot();
